import { useAccount } from '../../contexts/AccountContext';
import { scopeKeyLabel, type ExtranetRole, type RoleScope, type ScopeKey } from '../../domain/access';
import { MultiSelect } from '../common/MultiSelect';

interface ScopeEditorProps {
  /** Rôle attribué : son périmètre est plafonné par celui de l'appelant sur ce rôle. */
  role: ExtranetRole;
  value: RoleScope[];
  onChange: (next: RoleScope[]) => void;
  disabled?: boolean;
}

/**
 * Périmètre d'un rôle, restreint aux dimensions et valeurs que l'appelant porte
 * lui-même (`/me.access`). On ne se base **pas** sur `/me.filters` : celui-ci
 * déclare aussi des dimensions de filtrage de données — la réf. facturation 2
 * (contrat / CTR) par exemple — qui ne sont pas des périmètres attribuables.
 *
 * Une valeur déjà attribuée au membre mais absente du plafond de l'appelant
 * reste affichée : elle est conservée à l'enregistrement, la masquer la
 * supprimerait en silence.
 */
export const ScopeEditor = ({ role, value, onChange, disabled }: ScopeEditorProps) => {
  const { grantableScopesFor } = useAccount();
  const grantable = grantableScopesFor(role);

  const valuesFor = (key: ScopeKey) => value.find(scope => scope.key === key)?.values ?? [];

  const setValues = (key: ScopeKey, values: string[]) => {
    const others = value.filter(scope => scope.key !== key);
    onChange(values.length > 0 ? [...others, { key, values }] : others);
  };

  // Dimensions à afficher : celles que l'appelant peut déléguer, plus celles
  // déjà portées par le membre.
  const keys = Array.from(new Set([...grantable.map(s => s.key), ...value.map(s => s.key)]));

  if (keys.length === 0) {
    return (
      <p className="text-[11px] text-zinc-500">
        Votre compte ne porte aucune restriction de périmètre : ce profil donnera accès à tout le compte.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {keys.map(key => {
          const selected = valuesFor(key);
          const options = Array.from(
            new Set([...(grantable.find(scope => scope.key === key)?.values ?? []), ...selected]),
          );
          return (
            <MultiSelect
              key={key}
              label={scopeKeyLabel(key)}
              placeholder="Toutes"
              options={options.map(option => ({ value: option, label: option, mono: true }))}
              value={selected}
              onChange={values => setValues(key, values)}
              disabled={disabled}
            />
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-500">
        Aucune valeur sélectionnée = tout votre périmètre sur cette dimension.
      </p>
    </div>
  );
};
